import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePendingRequests, useNotificationWindows } from '@plug-core/state'
import EthereumIcon from '@plug-core/assets/icons/eth.svg'
import { isExternalPopup } from '@utils/windows'
import { initializeMessenger } from '@core/messengers'

import {
  Alert,
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import Button from '@components/Button'
import CanisterCall from './components/CanisterCall'
import useWallet from '@plug-core/hooks/useWallet'
import { shortAddress } from '@plug-core/utils/shortAddress'
import Transfer from './components/Transfer'
import { XTC_CANISTER_ID } from '@plug-core/constants/canisterIds'
import ErrorPopup from '@components/ErrorPopup'
import Touchable from '@components/Touchable'
import useConfig from '@plug-core/hooks/useConfig'
import TokenAvatar from '@components/TokenAvatar'
import { getCanisterInfo } from '@funded-labs/dab-js'
import getAgent from '@plug-core/utils/getAgent'
import { logger } from '@plug-core/logger'
import { Scope } from '@plug-core/interfaces/permissions'
import TransferEth from './components/TransferEth'
import usePreviousValue from '@hooks/usePreviousValue'
import { ROUTES } from '@utils/routes'

const backgroundMessenger = initializeMessenger({ connect: 'background' })

interface CanisterMetadata {
  url: string
  name: string
  description: string
  version: number
  logo_url: string
  canisterId: string
}

function hexToText(hexString: string): string {
  hexString = hexString.startsWith('0x') ? hexString.slice(2) : hexString

  return decodeURIComponent(hexString.replace(/\s+/g, '').replace(/[0-9a-f]{2}/g, '%$&'))
}

const CanisterRow = ({ canisterId }: { canisterId: string }) => {
  const { nfts, tokens } = useConfig()
  const [canisterData, setCanisterData] = useState<CanisterMetadata>()

  const nft = nfts.find((nft) => nft.principal_id.toString() === canisterId)
  const token = tokens.find((token) => token.address === canisterId)

  useEffect(() => {
    if (!token && !nft) {
      getCanisterInfo({ agent: getAgent(), canisterId: canisterId })
        .then(setCanisterData)
        .catch((e) => logger.error('Unable to fetch canister info', { originalError: e }))
    }
  }, [canisterId, nft, token])

  const logo = useMemo(() => {
    return canisterData?.logo_url || nft?.icon || token?.logo
  }, [canisterData, nft, token])

  const name = useMemo(() => {
    return canisterData?.name || nft?.name || token?.name
  }, [canisterData, nft, token])

  return (
    <ListItem>
      <ListItemAvatar>
        <TokenAvatar size={30} logo={logo} />
      </ListItemAvatar>
      <ListItemText primary={name} secondary={canisterId} />
    </ListItem>
  )
}

const Authorize = () => {
  const { pendingRequests, removePendingRequest } = usePendingRequests()
  const { notificationWindows } = useNotificationWindows()
  const [alertVisible, setAlertVisible] = useState(false)

  const {
    wallet: { name, principalId, ethAddress },
  } = useWallet()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const filteredRequests = useMemo(() => {
    return isExternalPopup
      ? pendingRequests.filter((request) => {
          return request.meta?.sender?.tab?.id === Number(window.location.search.split('tabId=')[1])
        })
      : pendingRequests
  }, [pendingRequests])

  const [pendingRequest, setPendingRequest] = useState(
    filteredRequests[filteredRequests.length - 1]
  )

  const prevPending = usePreviousValue(pendingRequest)

  const handleRequestAction = useCallback(() => {
    const nextPendingRequests = filteredRequests.filter((r) => r.id !== pendingRequest.id)
    removePendingRequest(pendingRequest.id)
    setPendingRequest(nextPendingRequests[nextPendingRequests.length - 1])
  }, [
    filteredRequests,
    pendingRequest?.id,
    pendingRequest?.meta?.sender?.tab?.id,
    notificationWindows,
    pendingRequests.length,
  ])

  useEffect(() => {
    const notificationWindow =
      notificationWindows[Number(prevPending?.meta?.sender?.tab?.id)?.toString()]

    if (filteredRequests.length === 0) {
      // Close tab with timeout - this allows requests that income instantly be processed
      // without rejected with Tab Closed
      // New instant incomming request will clear this timeout

      const timeout = setTimeout(async () => {
        navigate(ROUTES.HOME)
        if (notificationWindow?.id && chrome?.windows) {
          try {
            await chrome.windows.remove(notificationWindow.id)
          } catch (e) {
            logger.debug('Failed to close window', { originalError: e })
          }
        }
      }, 200)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [filteredRequests, prevPending])

  const approveRequest = useCallback(() => {
    backgroundMessenger.send(`message:${pendingRequest.id}`, true)
    handleRequestAction()
  }, [handleRequestAction, pendingRequest?.id])

  const rejectRequest = useCallback(() => {
    backgroundMessenger.send(`message:${pendingRequest.id}`, null)
    handleRequestAction()
  }, [handleRequestAction, pendingRequest?.id])

  useEffect(() => {
    if (pendingRequest?.method === 'unlock') approveRequest()
  }, [pendingRequest])

  const renderRequestPermissions = () => {
    if (!pendingRequest?.meta) return

    const hasGlobalDelegationRequest = !!pendingRequest.params?.scopes.find(
      (scope: Scope) => scope.method === 'icrc34_get_global_delegation'
    )

    return (
      <Stack>
        <Divider textAlign="center">
          <Chip label="Permissions" />
        </Divider>
        <Stack spacing={1} px={2}>
          {hasGlobalDelegationRequest && (
            <Touchable onClick={() => setAlertVisible(true)}>
              <Alert severity="warning" sx={{ alignItems: 'center' }}>
                {t('authorizationRequest.globalDelegationWarningTitle')}
                <Typography fontSize={12}>{t('authorizationRequest.readMore')}</Typography>
              </Alert>
            </Touchable>
          )}
          {pendingRequest.params.scopes?.map((scope: Scope, index: number) => (
            <Stack component={Paper} spacing={1} p={1} mx={1} key={index}>
              <Typography fontWeight="bold" ml={1}>
                {`${index + 1}. ${t(`authorizationRequest.${scope.method}`)}`}
              </Typography>
              {scope.targets && (
                <>
                  <Divider />
                  <Typography>For canisters:</Typography>
                  {scope.targets.map((target) => (
                    <CanisterRow canisterId={target} key={target} />
                  ))}
                </>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    )
  }

  const renderPersonalSign = (message: string) => {
    return (
      <Stack spacing={3}>
        <Divider>
          <Chip label="Sign message" />
        </Divider>
        <Stack spacing={2}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('approveTransaction.personal_sign.title')}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {message}
            </Typography>
          </Paper>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('approveTransaction.personalSign.additionalInformation')}
            </Typography>
            <Typography variant="body2">
              • {t('approveTransaction.personal_sign.gasInfo')}
            </Typography>
            <Typography variant="body2">
              • {t('approveTransaction.personal_sign.warning')}
            </Typography>
          </Paper>
        </Stack>
      </Stack>
    )
  }

  const renderDappInfo = () => {
    if (!pendingRequest?.meta) return
    const { tab, url } = pendingRequest.meta.sender

    const dappUrl = new URL(url || '')

    if (
      pendingRequest?.method == 'eth_accounts' ||
      pendingRequest?.params?.scopes?.some((scope: Scope) => scope.method === 'eth_accounts')
    ) {
      return (
        <>
          <ListItem>
            <ListItemAvatar>
              <Avatar sx={{ width: 50, height: 50, marginRight: 2 }} src={tab?.favIconUrl} />
            </ListItemAvatar>
            <ListItemText
              primary={t(`${pendingRequest?.params?.method || pendingRequest?.method}`)}
              secondary={dappUrl.host}
              primaryTypographyProps={{ variant: 'h6' }}
            />
          </ListItem>
          <Stack spacing={2} px={2}>
            <Box display="flex" alignItems="center">
              <Avatar src={EthereumIcon} sx={{ width: 24, height: 24, marginRight: 1 }} />
              <Typography color={(theme) => theme.palette.grey[500]} fontWeight="light">
                {t(`Ethereum Mainnet`)}
              </Typography>
            </Box>
            <Paper component={ListItem}>
              <ListItemText primary="Account" />
              <ListItemText
                secondary={shortAddress(ethAddress || '')}
                secondaryTypographyProps={{ textAlign: 'right' }}
              />
            </Paper>
          </Stack>
        </>
      )
    }

    return (
      <>
        <ListItem>
          <ListItemAvatar>
            <Avatar sx={{ width: 50, height: 50, marginRight: 2 }} src={tab?.favIconUrl} />
          </ListItemAvatar>
          <ListItemText
            primary={t(`approveTransaction.${pendingRequest?.method}.title`)}
            secondary={dappUrl.host}
            primaryTypographyProps={{ variant: 'h6' }}
          />
        </ListItem>
        <Stack spacing={2} px={2}>
          <Typography color={(theme) => theme.palette.grey[500]} fontWeight="light">
            {t(`approveTransaction.${pendingRequest?.method}.description`)}
          </Typography>
          <Paper component={ListItem}>
            <ListItemText primary="Account" />
            <ListItemText
              secondary={name || shortAddress(principalId)}
              secondaryTypographyProps={{ textAlign: 'right' }}
            />
          </Paper>
        </Stack>
      </>
    )
  }

  const renderCanisterCall = () => {
    const { canisterId, method, arg } = pendingRequest.params

    return <CanisterCall method={method} canisterId={canisterId} arg={arg} />
  }

  const renderDelegationPermissions = () => (
    <Stack px={2} mt={2}>
      {pendingRequest.params?.targets?.length > 0 && (
        <>
          <Touchable onClick={() => setAlertVisible(true)}>
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              {t('authorizationRequest.globalDelegationWarningTitle')}
              <Typography fontSize={12}>{t('authorizationRequest.readMore')}</Typography>
            </Alert>
          </Touchable>
          <Typography mt={1}>Target canisters:</Typography>
        </>
      )}
      <List>
        {(pendingRequest.params?.targets || []).map((canister: string) => (
          <CanisterRow canisterId={canister} key={canister} />
        ))}
      </List>
    </Stack>
  )

  const renderRequestEthTransfer = () => {
    return <TransferEth transfer={pendingRequest.params.transaction} />
  }

  const renderRequestTransfer = () => {
    return (
      <Transfer
        transferParams={pendingRequest.params.transferParams}
        tokenCanisterId={pendingRequest.params.tokenCanisterId}
      />
    )
  }

  const renderBurnRequest = () => {
    return (
      <Transfer
        transferParams={pendingRequest.params.burnParams}
        tokenCanisterId={XTC_CANISTER_ID}
      />
    )
  }

  const renderActionBody = () => {
    switch (pendingRequest?.method) {
      case 'wallet_requestPermissions':
      case 'icrc25_request_permissions':
        return renderRequestPermissions()
      case 'icrc34_delegation':
        return renderDelegationPermissions()
      case 'icrc49_call_canister':
      case 'icrc49_call_canister_compatibility':
        return renderCanisterCall()
      case 'request_transfer':
        return renderRequestTransfer()
      case 'request_burn_xtc':
        return renderBurnRequest()
      case 'eth_accounts':
        switch (pendingRequest.params.method) {
          case 'eth_sendTransaction':
            return renderRequestEthTransfer()
          case 'personal_sign':
            return renderPersonalSign(hexToText(pendingRequest.params.message))
          default:
            return null
        }
    }
  }

  return (
    <Stack height="100%">
      {renderDappInfo()}
      <Stack flex={1} overflow="scroll">
        {renderActionBody()}
      </Stack>
      <Stack bgcolor={(theme) => theme.palette.background.mid} px={2} py={2} spacing={0.5}>
        <Button onClick={approveRequest} variant="rainbow" fullWidth disabled={!pendingRequest}>
          Approve
        </Button>
        <Button onClick={() => rejectRequest()} fullWidth disabled={!pendingRequest}>
          Reject
        </Button>
      </Stack>
      <ErrorPopup
        title="Warning!"
        description={t('authorizationRequest.globalDelegationWarning')}
        visible={alertVisible}
        onDismiss={() => setAlertVisible(false)}
      />
    </Stack>
  )
}

export default Authorize
